import { useState, useRef } from "react";

// ─── Token map ───────────────────────────────────────────────────────────────
const CARD_TOKENS = {
  robbery:   { bg: "#FFF5F5", bgDark: "#2a1515", border: "#F7C1C1", borderDark: "#791F1F", text: "#501313", textDark: "#F7C1C1", dot: "#E24B4A", statBg: "rgba(226,75,74,.1)"  },
  fraud:     { bg: "#FFF5F5", bgDark: "#2a1515", border: "#F7C1C1", borderDark: "#791F1F", text: "#501313", textDark: "#F7C1C1", dot: "#E24B4A", statBg: "rgba(226,75,74,.1)"  },
  archetype: { bg: "#F4F3FF", bgDark: "#18152a", border: "#CECBF6", borderDark: "#534AB7", text: "#26215C", textDark: "#CECBF6", dot: "#7F77DD", statBg: "rgba(127,119,221,.1)" },
  collapse:  { bg: "#FFFBF0", bgDark: "#2a2010", border: "#FAC775", borderDark: "#854F0B", text: "#412402", textDark: "#FAC775", dot: "#BA7517", statBg: "rgba(186,117,23,.1)"  },
  rivalry:   { bg: "#F0F6FF", bgDark: "#101e2a", border: "#B5D4F4", borderDark: "#185FA5", text: "#042C53", textDark: "#B5D4F4", dot: "#378ADD", statBg: "rgba(55,138,221,.1)"  },
  achieve:   { bg: "#F3FAF0", bgDark: "#111a0a", border: "#C0DD97", borderDark: "#3B6D11", text: "#173404", textDark: "#C0DD97", dot: "#639922", statBg: "rgba(99,153,34,.1)"   },
  coldcase:  { bg: "#FFF5F5", bgDark: "#2a1515", border: "#F7C1C1", borderDark: "#791F1F", text: "#501313", textDark: "#F7C1C1", dot: "#E24B4A", statBg: "rgba(226,75,74,.1)"  },
  pattern:   { bg: "#F4F3FF", bgDark: "#18152a", border: "#CECBF6", borderDark: "#534AB7", text: "#26215C", textDark: "#CECBF6", dot: "#7F77DD", statBg: "rgba(127,119,221,.1)" },
  era_end:   { bg: "#FFFBF0", bgDark: "#2a2010", border: "#FAC775", borderDark: "#854F0B", text: "#412402", textDark: "#FAC775", dot: "#BA7517", statBg: "rgba(186,117,23,.1)"  },
  wrap:      { bg: "#F3FAF0", bgDark: "#111a0a", border: "#C0DD97", borderDark: "#3B6D11", text: "#173404", textDark: "#C0DD97", dot: "#639922", statBg: "rgba(99,153,34,.1)"   },
};

const CARD_LABELS = {
  robbery:   "Trade robbery",
  fraud:     "Fraud alert",
  archetype: "Identity shift",
  collapse:  "Collapse watch",
  rivalry:   "Rivalry",
  achieve:   "Achievement",
  coldcase:  "Cold case",
  pattern:   "Pattern detected",
  era_end:   "Era ended",
  wrap:      "Season wrap",
};

// ─── Trigger thresholds (used server-side, exported for reference) ────────────
export const CARD_TRIGGERS = {
  robbery:   (data) => data.tradeDelta > 25,
  fraud:     (data) => data.tradeDelta > 28 && data.hoursSinceLoss < 48,
  archetype: (data) => data.driftDimensions >= 3,
  collapse:  (data) => data.collapseSignals >= 3 && data.firstRoundPicks === 0,
  rivalry:   (data) => data.tradeRejections >= 3 || (data.h2hWinRate < 0.25 && data.h2hGames >= 6),
  achieve:   (data) => data.consecutiveWaiverWins >= 6,
  coldcase:  () => true, // weekly, server-scheduled
  pattern:   (data) => data.postLossActivityRate > 0.70,
  era_end:   (data) => data.contentionDrop >= 25,
  wrap:      (data) => data.week >= 17 || data.playoffEliminated,
};

// ─── Main component ───────────────────────────────────────────────────────────
/**
 * IntelCard
 *
 * @param {object}  props
 * @param {'robbery'|'fraud'|'archetype'|'collapse'|'rivalry'|'achieve'|'coldcase'|'pattern'|'era_end'|'wrap'} props.type
 * @param {string}  props.managerName   — display handle, e.g. "@JakeDawson"
 * @param {string}  props.leagueName    — shown in footer
 * @param {number}  props.week          — current week number
 * @param {string}  props.season        — e.g. "2024"
 * @param {string}  props.headline      — primary headline text
 * @param {string}  props.subtext       — supporting detail (1–2 sentences)
 * @param {string}  [props.badge]       — optional badge label (e.g. "Active", "3/5 signals")
 * @param {{ value: string; label: string }[]} props.stats  — exactly 2 stat objects
 * @param {boolean} [props.darkMode]    — override system dark mode detection
 * @param {function} [props.onShare]    — called with canvas blob after share
 */
export default function IntelCard({
  type = "robbery",
  managerName = "@Manager",
  leagueName = "The League",
  week = 9,
  season = "2024",
  headline = "Something happened",
  subtext = "Details about the event go here.",
  badge = null,
  stats = [{ value: "—", label: "Stat one" }, { value: "—", label: "Stat two" }],
  darkMode = false,
  onShare,
}) {
  const cardRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const tk = CARD_TOKENS[type] ?? CARD_TOKENS.robbery;
  const bg        = darkMode ? tk.bgDark     : tk.bg;
  const border    = darkMode ? tk.borderDark : tk.border;
  const color     = darkMode ? tk.textDark   : tk.text;

  // ── Share: render card to canvas → copy PNG to clipboard ──────────────────
  const handleShare = async () => {
    if (!cardRef.current) return;
    try {
      // Dynamically import html2canvas to keep bundle lean
      const { default: h2c } = await import("https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.esm.js");
      const canvas = await h2c(cardRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
        width: 340,
        height: cardRef.current.offsetHeight,
      });
      canvas.toBlob(async (blob) => {
        if (!blob) return;
        try {
          await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch {
          // Fallback: trigger download
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `leagueintel_${type}_${managerName.replace("@","")}_w${week}.png`;
          a.click();
          URL.revokeObjectURL(url);
        }
        onShare?.(blob);
      }, "image/png");
    } catch (err) {
      console.error("IntelCard share failed:", err);
    }
  };

  // ── Native share sheet (mobile) ────────────────────────────────────────────
  const handleNativeShare = async () => {
    if (!navigator.share || !cardRef.current) { handleShare(); return; }
    const { default: h2c } = await import("https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.esm.js");
    const canvas = await h2c(cardRef.current, { scale: 2, backgroundColor: null });
    canvas.toBlob(async (blob) => {
      const file = new File([blob], `leagueintel_${type}.png`, { type: "image/png" });
      await navigator.share({ files: [file], title: `LeagueIntel · ${CARD_LABELS[type]}`, text: headline });
    }, "image/png");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>

      {/* ── Card surface (this is what gets screenshotted) ── */}
      <div
        ref={cardRef}
        style={{
          width: 340,
          borderRadius: 16,
          padding: 20,
          border: `1px solid ${border}`,
          background: bg,
          color,
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        }}
      >
        {/* Type label row */}
        <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10, display: "flex", alignItems: "center", gap: 6, color }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: tk.dot, flexShrink: 0 }} />
          LeagueIntel · {CARD_LABELS[type]}
        </div>

        {/* Optional badge */}
        {badge && (
          <div style={{ display: "inline-block", fontSize: 10, fontWeight: 500, padding: "3px 10px", borderRadius: 10, background: tk.statBg, color, border: `0.5px solid ${border}`, marginBottom: 10 }}>
            {badge}
          </div>
        )}

        {/* Headline */}
        <div style={{ fontSize: 18, fontWeight: 500, lineHeight: 1.25, marginBottom: 6, color }}>
          {headline}
        </div>

        {/* Subtext */}
        <div style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 14, color, opacity: 0.85 }}>
          {subtext}
        </div>

        {/* Stats (always exactly 2) */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
          {stats.slice(0, 2).map((s, i) => (
            <div key={i} style={{ borderRadius: 10, padding: "10px 12px", background: tk.statBg }}>
              <div style={{ fontSize: 20, fontWeight: 500, lineHeight: 1, marginBottom: 2, color }}>{s.value}</div>
              <div style={{ fontSize: 10, opacity: 0.7, color }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ fontSize: 10, opacity: 0.5, paddingTop: 10, borderTop: `0.5px solid ${color}`, color, display: "flex", justifyContent: "space-between" }}>
          <span>leagueintel.gg · {leagueName}</span>
          <span>Week {week} · {season}</span>
        </div>
      </div>

      {/* ── Action row (outside the screenshot area) ── */}
      <div style={{ display: "flex", gap: 8, width: 340 }}>
        <button
          onClick={handleShare}
          style={{ flex: 1, fontSize: 13, padding: "9px 0", borderRadius: 8, border: "0.5px solid #ddd", background: "#fff", cursor: "pointer", fontWeight: 500 }}
        >
          {copied ? "Copied!" : "Copy image"}
        </button>
        <button
          onClick={handleNativeShare}
          style={{ flex: 1, fontSize: 13, padding: "9px 0", borderRadius: 8, border: "none", background: "#111", color: "#fff", cursor: "pointer", fontWeight: 500 }}
        >
          Share
        </button>
      </div>
    </div>
  );
}

// ─── Usage examples ───────────────────────────────────────────────────────────
//
// <IntelCard
//   type="robbery"
//   managerName="@JakeDawson"
//   leagueName="Dynasty Wolves"
//   week={9}
//   season="2024"
//   headline="@JakeDawson just got robbed"
//   subtext="Trade value differential: 34 pts. Largest single-deal loss in league history."
//   stats={[{ value: "−34 pts", label: "Value lost" }, { value: "#1 worst", label: "All-time rank" }]}
// />
//
// <IntelCard
//   type="archetype"
//   managerName="@TheProphet"
//   headline="@TheProphet is no longer The Prophet"
//   subtext="3 seasons of patience. Assets matured. New classification: The Asset Vampire."
//   badge={null}
//   stats={[{ value: "3 yrs", label: "Prophet era" }, { value: "4 deals", label: "Triggered shift" }]}
// />
